import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'tapes-backoffice-theme';

// Read once at module load (before first React render) so the initial paint
// already matches the user's preference / no flash of wrong theme.
function readStored(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch { /* localStorage blocked */ }
  return 'dark';
}

function apply(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'light') root.classList.add('light');
  else                   root.classList.remove('light');
}

// Apply synchronously at module evaluation time so the first paint is correct.
// `main.tsx` imports this hook (transitively) before <App /> mounts.
const initialTheme = readStored();
apply(initialTheme);

let listeners: Array<(t: Theme) => void> = [];

export function useTheme(): { theme: Theme; toggle: () => void; set: (t: Theme) => void } {
  const [theme, setLocal] = useState<Theme>(initialTheme);

  useEffect(() => {
    const onChange = (t: Theme) => setLocal(t);
    listeners.push(onChange);
    return () => { listeners = listeners.filter((l) => l !== onChange); };
  }, []);

  function set(next: Theme) {
    apply(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
    listeners.forEach((l) => l(next));
  }

  function toggle() { set(theme === 'dark' ? 'light' : 'dark'); }

  return { theme, toggle, set };
}
